import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type { LessonsFilters } from '@ts-lessons/interfaces'
import type { Status as BookingStatus, Type as BookingType } from '@brighterly/lib-core-types/booking/enums'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { LessonsSortDirection } from '@ts-lessons/enums'

import { getFirstQueryValue, normalizeArrayQuery, toCountValue } from '@helpers/route'
import dayjs from 'dayjs'

/** Array filters that links from app-main encode as `key[0]=…`. */
export const LESSONS_ARRAY_QUERY_KEYS: readonly string[] = ['tutor', 'type', 'status']

/** The "Unassigned tutor" option: the API reads tutor 0 as "no tutor assigned". */
export const LESSONS_UNASSIGNED_TUTOR_ID = 0

export const LESSONS_DAYS_OF_WEEK: readonly string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const FILTER_QUERY_KEYS: readonly string[] = ['from', 'to', 'tutor', 'type', 'status', 'sort_direction', 'day_of_week']
const DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/

/** The two booking lists the filters are checked against. */
interface LessonsQueryLists {
  type: Record<string, string>
  status: Record<string, string>
}

interface LessonsFilterParams {
  from?: string
  to?: string
  tutor?: number[]
  type?: string[]
  status?: string[]
  sort_direction?: string
  day_of_week?: string
}

const toDateTimeValue = (value: unknown): string | null => {
  const raw = getFirstQueryValue(value)
  return raw !== null && DATE_TIME_PATTERN.test(raw) && dayjs(raw).isValid() ? raw : null
}

const isCount = (value: number | null): value is number => value !== null

export const emptyLessonsFilters = (): LessonsFilters => ({
  from: null,
  to: null,
  tutorIds: [],
  types: [],
  statuses: [],
  sortDirection: LessonsSortDirection.Desc,
  dayOfWeek: null,
})

/** The API narrows by date only with both bounds, so a lone bound is dropped rather than shown as a filter. */
export const queryToLessonsFilters = (query: Record<string, unknown>, lists: LessonsQueryLists | null): LessonsFilters => {
  const from = toDateTimeValue(query.from)
  const to = toDateTimeValue(query.to)
  const types = normalizeArrayQuery(query.type) ?? []
  const statuses = normalizeArrayQuery(query.status) ?? []
  const dayOfWeek = getFirstQueryValue(query.day_of_week)

  return {
    from: from && to ? from : null,
    to: from && to ? to : null,
    tutorIds: [...new Set((normalizeArrayQuery(query.tutor) ?? []).map(toCountValue).filter(isCount))],
    types: (lists ? types.filter(type => type in lists.type) : types) as BookingType[],
    statuses: (lists ? statuses.filter(status => status in lists.status) : statuses) as BookingStatus[],
    sortDirection: getFirstQueryValue(query.sort_direction) === LessonsSortDirection.Asc
      ? LessonsSortDirection.Asc
      : LessonsSortDirection.Desc,
    dayOfWeek: dayOfWeek && LESSONS_DAYS_OF_WEEK.includes(dayOfWeek) ? dayOfWeek : null,
  }
}

/** DESC is the API default, so it is left out of the request and of the URL. */
export const getLessonsFilterParams = (filters: LessonsFilters): LessonsFilterParams => ({
  from: filters.from && filters.to ? filters.from : undefined,
  to: filters.from && filters.to ? filters.to : undefined,
  tutor: filters.tutorIds.length ? filters.tutorIds : undefined,
  type: filters.types.length ? filters.types : undefined,
  status: filters.statuses.length ? filters.statuses : undefined,
  sort_direction: filters.sortDirection === LessonsSortDirection.Asc ? LessonsSortDirection.Asc : undefined,
  day_of_week: filters.dayOfWeek ?? undefined,
})

/** Every filter key is present, an empty one as `undefined`, so spreading it over the route query clears it. */
export const lessonsFiltersToQuery = (filters: LessonsFilters): LocationQueryRaw => {
  const params = getLessonsFilterParams(filters)

  return {
    ...params,
    tutor: params.tutor?.map(String),
  }
}

const toComparable = (value: unknown): string => JSON.stringify(normalizeArrayQuery(value) ?? [])

export const getCanonicalLessonsQuery = (query: LocationQuery, filters: LessonsFilters): LocationQueryRaw | null => {
  const canonical = lessonsFiltersToQuery(filters)
  const isCanonical = FILTER_QUERY_KEYS.every(key => toComparable(query[key]) === toComparable(canonical[key]))

  return isCanonical ? null : { ...query, ...canonical }
}

export const toLessonsDateRange = (from: string | null, to: string | null): Date[] | null => {
  const fromDate = from ? dayjs(from) : null
  const toDate = to ? dayjs(to) : null

  if (!fromDate?.isValid() || !toDate?.isValid()) return null

  return [fromDate.toDate(), toDate.toDate()]
}

export const fromLessonsDateRange = (value: (Date | null | undefined)[] | null): Pick<LessonsFilters, 'from' | 'to'> => {
  const toBound = (date: Date | null | undefined): string | null =>
    date && dayjs(date).isValid() ? dayjs(date).format(DateFormat.Default) : null

  const from = toBound(value?.[0])
  const to = toBound(value?.[1])

  return {
    from: from && to ? from : null,
    to: from && to ? to : null,
  }
}
